import { readableDate, Storage } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { Icon, IconButton, Link, Modal, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';

import clsx from 'clsx';
import Joi from 'joi';
import { useFilter } from '../../contexts/filters';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { useRosterFilesReport } from '../../entities/reports/queries';
import { TRosterFilesReportRow } from '../../generated/graphql';
import { companyFilter, monthFilter } from '../reports/reportFilters';
import { Step4 } from './import/Step4';
import styles from './ImportHistory.module.scss';

type TValidatedRowData = {
  value?: Record<string, any>;
  error?: Joi.ValidationError;
};

type TChangelogModalInfo = {
  changelog: Record<string, TValidatedRowData[]>;
  mapping: Record<number, string | undefined>;
  createdAt?: string | null;
  companyId?: string | null;
  companyName?: string | null;
};

export const ImportHistory = () => {
  const { filtersPageMode } = useFilter();

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [companyId, setCompanyId] = useState<string>();

  const { data: rosterHistory, error: reportError, loading: reportLoading } = useRosterFilesReport(month, companyId);
  const { data: companies, error: companiesError, loading: companiesLoading } = useQueryListAllCompaniesOptions();

  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [changelogModalInfo, setChangelogModalInfo] = useState<TChangelogModalInfo>({ changelog: {}, mapping: {} });

  const filters = useMemo<TFiltersProps['filters']>(() => [monthFilter(month, setMonth), companyFilter(companies, companyId, setCompanyId)], [month, companies, companyId]);

  const getRowId = useCallback((rosterFile: TRosterFilesReportRow) => {
    return `${rosterFile.companyName}_${rosterFile.createdAt}_${rosterFile.companyId}`;
  }, []);

  const changelogInfo = useCallback(async (cell: TRosterFilesReportRow) => {
    if (!cell.changelogFile) return;
    const { changelogFile, createdAt, mapping, companyId, companyName } = cell;
    const { key, bucket, region } = changelogFile;
    if (!key || !bucket || !region) return;
    try {
      const signedURL = await Storage.get(key, { bucket, region });
      const response = await fetch(signedURL);
      if (!response.ok) throw new Error(`Could not load changelog file: ${response.status}.`);
      const text = await response.text();
      // Parse the complete file before opening the changelog modal.
      try {
        setChangelogModalInfo({ changelog: JSON.parse(text ?? '{}'), createdAt, mapping: JSON.parse(mapping ?? '{}'), companyId, companyName });
      } catch {
        console.warn(`Could not parse changelog file for import (${cell.companyName}_${cell.createdAt}_${cell.companyId}).`);
        setChangelogModalInfo({ changelog: {}, createdAt, mapping: {}, companyId, companyName });
      }
      setIsModalOpen(true);
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error opening the file.');
    }
  }, []);

  return (
    <TablePage dataLoading={reportLoading || companiesLoading} error={reportError || companiesError} title='Import History'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TRosterFilesReportRow>
          enableSorting={true}
          defaultSorting={{ id: 'importStartedAt', desc: true }}
          data={rosterHistory}
          emptyText={rosterHistory.length === 0 ? 'No import files uploaded.' : 'No import files for the current filters.'}
          expandable={false}
          fillSelectedRows={false}
          fixedHeader={true}
          getRowId={getRowId}
          hideSubRowsSelect={true}
          hoverableRows={false}
          selectableRows={false}
          virtual={false}
          columns={[
            {
              header: 'Company',
              accessorKey: 'companyName',
              cell: ({ cell }) => {
                return <div className={clsx(tableClassNames.primary)}> {cell.row.original.companyName || '-'}</div>;
              }
            },
            {
              header: 'File Name',
              accessorKey: 'file.key',
              cell: ({ cell }) => {
                const { bucket, key, region } = cell.row.original.file;
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    {key ? (
                      <Link
                        label={key?.split('/').pop() as string}
                        onClick={async () => {
                          try {
                            const signedURL = await Storage.get(key, { bucket, region });
                            open(signedURL, '_blank');
                          } catch (error) {
                            console.error(serializeError(error));
                            toast.error('There was an error opening the file.');
                          }
                        }}
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                );
              }
            },
            {
              header: 'Import Start At',
              accessorKey: 'importStartedAt',
              cell: ({ cell }) => {
                const { importStartedAt } = cell.row.original;
                return <div className={clsx(tableClassNames.secondary)}>{readableDate(importStartedAt, 'do MMM, yyyy hh:mmaaa')}</div>;
              }
            },
            {
              header: 'Import Ended At',
              accessorKey: 'impoortEndedAt',
              cell: ({ cell }) => {
                const { impoortEndedAt } = cell.row.original;
                return <div className={clsx(tableClassNames.secondary)}>{readableDate(impoortEndedAt, 'do MMM, yyyy hh:mmaaa')}</div>;
              }
            },
            {
              header: 'Errors',
              accessorKey: 'importErrors',
              cell: ({ cell }) => {
                const { bucket, key } = JSON.parse(cell.row.original.importErrors ?? '{}');
                return (
                  <div className={clsx(tableClassNames.quaternary)}>
                    {!cell.row.original.importErrors ? (
                      '-'
                    ) : bucket && key ? (
                      <Link
                        label={'Errors file'}
                        onClick={async () => {
                          try {
                            const signedURL = await Storage.get(key, { bucket, region: 'ca-central-1' });
                            open(signedURL, '_blank');
                          } catch (error) {
                            console.error(serializeError(error));
                            toast.error('There was an error opening the file.');
                          }
                        }}
                      />
                    ) : (
                      "Couldn't parse errors."
                    )}
                  </div>
                );
              }
            },
            {
              header: 'Status',
              id: 'status',
              enableSorting: false,
              cell: ({ cell }) => {
                const { importStartedAt, impoortEndedAt, importErrors } = cell.row.original;
                const thisImportErrors = JSON.parse(typeof importErrors === 'string' ? importErrors : '{}');
                const StatusTooltip = () => {
                  if (!importStartedAt)
                    // PENDING
                    return (
                      <Tooltip message='The import will start soon.'>
                        <Icon className={styles.pending} icon='pending' />
                      </Tooltip>
                    );
                  if (!impoortEndedAt)
                    // RUNNING
                    return (
                      <Tooltip message='Import running.'>
                        <Icon className={styles.running} icon='cached' />
                      </Tooltip>
                    );
                  if (!_.isEmpty(thisImportErrors) && importErrors)
                    // ERROR
                    return (
                      <Tooltip message='Import finished with errors.'>
                        <Icon className={styles.error} icon='error' />
                      </Tooltip>
                    );
                  // SUCCEEDED
                  return (
                    <Tooltip message='Import finished successfully.'>
                      <Icon className={styles.success} icon='check_circle' />
                    </Tooltip>
                  );
                };

                return (
                  <div className={clsx(tableClassNames.quaternary)}>
                    <StatusTooltip />
                  </div>
                );
              }
            },
            {
              header: 'Changelog',
              id: 'changelog',
              enableSorting: false,
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.quaternary)}>
                    <Tooltip message={cell.row.original.changelog || cell.row.original.changelogFile ? 'View history log of this import.' : 'History log not found.'}>
                      <IconButton
                        disabled={!cell.row.original.changelog && !cell.row.original.changelogFile}
                        className={clsx(styles.success, styles.sm)}
                        icon='history'
                        onClick={async () => {
                          await changelogInfo(cell.row.original);
                        }}
                      />
                    </Tooltip>
                  </div>
                );
              }
            }
          ]}
        />
      </div>
      <Modal
        headline={`Import changes of ${changelogModalInfo.companyName ?? '{not found}'} at ${readableDate(changelogModalInfo.createdAt, 'do MMM, yyyy hh:mmaaa')}`}
        open={isModalOpen}
        size='large'
        closeModal={() => {
          setIsModalOpen(false);
        }}>
        <Step4 companyId={changelogModalInfo.companyId ?? '0'} mapping={changelogModalInfo.mapping} changelog={changelogModalInfo.changelog} />
      </Modal>
    </TablePage>
  );
};
